/**
 * FE-PR-20 — sign-in form handlers (wired from useSignInScreen).
 */
import type { FormEvent } from 'react';
import type { TFunction } from 'i18next';
import type { NavigateFunction } from 'react-router-dom';
import type { CustomerAnalyticsManager } from '../../../shared/analytics/useCustomerAnalyticsManager.js';
import {
  isFullAuthSessionResponse,
  isPendingOtherChannelResponse,
  type RequestOtpResponse,
} from '../authApi.js';
import type {
  useLoginWithPassword,
  useRequestOtp,
  useVerifyOtp,
} from '../authApi.js';
import {
  CustomerAuthErrorCodes,
  getApiErrorCode,
  getApiErrorLockedUntil,
  isAccountLockedError,
  isApiErrorCode,
} from '../authErrorCodes.js';
import { toCustomerFacingErrorMessage } from '../../../shared/errors/toCustomerFacingErrorMessage.js';
import {
  toAuthConsentsPayload,
  type RegistrationConsentsFormState,
} from '../authConsents.js';
import { shouldCollectOtpConsents } from '../otpConsentPolicy.js';
import {
  buildRemainingPendingRegistration,
  readPendingRegistration,
  savePendingRegistration,
} from '../pendingRegistrationStorage.js';
import {
  resolveCustomerSignUpPath,
  resolveCustomerSignUpPendingPath,
} from '../customerAuthPaths.js';
import {
  classifyPasswordLoginIdentifier,
  classifySignInIdentifier,
  createLoginPasswordSchema,
  otpConsentsSchema,
  requestOtpSchema,
  verifyOtpSchema,
  type PasswordContactPolicy,
} from '../validation.js';
import { buildConsentFieldErrors, maskEmailForPending } from '../presenters/signInScreenPresenter.js';

type TurnstileExecute = {
  readonly required: boolean;
  readonly execute: () => Promise<string | undefined>;
  readonly resetTurnstile: () => void;
};

type OtpMutation = ReturnType<typeof useRequestOtp>;
type VerifyMutation = ReturnType<typeof useVerifyOtp>;
type PasswordMutation = ReturnType<typeof useLoginWithPassword>;

export interface SignInScreenHandlerDeps {
  readonly t: TFunction<'customer'>;
  readonly tenantCode: string;
  readonly navigate: NavigateFunction;
  readonly analytics: CustomerAnalyticsManager;
  readonly turnstile: TurnstileExecute;
  readonly requestOtp: OtpMutation;
  readonly verifyOtp: VerifyMutation;
  readonly loginPassword: PasswordMutation;
  readonly accountHasLegalGrant: boolean | undefined;
  /** When true, persist follows the remember-me checkbox; when false, always persist. */
  readonly showRememberMe: boolean;
  /** G5 / G17 — E.164 password lookup when contact policy allows phone. */
  readonly passwordPhoneLookupPolicy: PasswordContactPolicy;
  /** Called after full session land with the authenticated customerId (React session may still be stale). */
  readonly onSignedIn?: (customerId: number) => void;
  readonly identifier: string;
  readonly password: string;
  readonly code: string;
  readonly rememberMe: boolean;
  readonly challenge: RequestOtpResponse | null;
  readonly showOtpConsents: boolean;
  readonly showPasswordConsents: boolean;
  readonly otpConsents: RegistrationConsentsFormState;
  readonly setIdentifierError: (value: string | undefined) => void;
  readonly setPasswordError: (value: string | undefined) => void;
  readonly setCodeError: (value: string | undefined) => void;
  /** G4 — form-level / API failures (banner only; never dual with FormErrorSummary). */
  readonly setFormError: (value: string | undefined) => void;
  readonly setDeliveryErrorCode: (
    value:
      | typeof CustomerAuthErrorCodes.OTP_DELIVERY_FAILED
      | typeof CustomerAuthErrorCodes.SMS_NOT_AVAILABLE
      | undefined,
  ) => void;
  readonly setCredentialsNotConfigured: (value: boolean) => void;
  readonly setEmailNotVerified: (value: boolean) => void;
  /** ACCOUNT_LOCKED / HTTP 423 first-class UX. */
  readonly setAccountLocked: (value: boolean) => void;
  /** ISO lockedUntil from API when present; clear with undefined. */
  readonly setAccountLockedUntil: (value: string | undefined) => void;
  readonly setShowOtpConsents: (value: boolean) => void;
  readonly setShowPasswordConsents: (value: boolean) => void;
  readonly setConsentFieldErrors: (value: Record<string, string>) => void;
  readonly setChallenge: (value: RequestOtpResponse | null) => void;
  readonly setOtpConsents: (value: RegistrationConsentsFormState) => void;
  readonly setLastAuthProvider: (provider: 'google' | 'apple' | 'otp' | 'password') => void;
}

export interface SignInScreenHandlers {
  readonly goToPendingVerification: () => void;
  readonly handleRequest: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  readonly handleVerify: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  readonly handlePasswordLogin: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}

async function resolveTurnstileToken(turnstile: TurnstileExecute): Promise<string | undefined> {
  try {
    return await turnstile.execute();
  } catch {
    turnstile.resetTurnstile();
    return undefined;
  }
}

function applyAccountLocked(deps: SignInScreenHandlerDeps, err: Error): void {
  deps.setAccountLocked(true);
  deps.setAccountLockedUntil(getApiErrorLockedUntil(err));
}

function clearAccountLocked(deps: SignInScreenHandlerDeps): void {
  deps.setAccountLocked(false);
  deps.setAccountLockedUntil(undefined);
}

export function createSignInScreenHandlers(deps: SignInScreenHandlerDeps): SignInScreenHandlers {
  const goToPendingVerification = (): void => {
    const classified = classifySignInIdentifier(deps.identifier);
    const masked = maskEmailForPending(deps.identifier);
    if (classified?.channel === 'email' && masked !== null) {
      savePendingRegistration(deps.tenantCode, {
        channel: 'email',
        channels: ['email'],
        identifier: classified.identifier,
        maskedIdentifier: masked,
      });
    }
    deps.navigate(resolveCustomerSignUpPendingPath(deps.tenantCode));
  };

  const handleRequest = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    deps.setLastAuthProvider('otp');
    deps.setIdentifierError(undefined);
    deps.setFormError(undefined);
    deps.setDeliveryErrorCode(undefined);
    deps.setEmailNotVerified(false);
    clearAccountLocked(deps);
    const parsed = requestOtpSchema.safeParse({ identifier: deps.identifier });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      deps.setIdentifierError(issue?.message ?? deps.t('validation.genericInvalid'));
      return;
    }
    const turnstileToken = await resolveTurnstileToken(deps.turnstile);
    if (deps.turnstile.required && (turnstileToken === undefined || turnstileToken.length === 0)) {
      return;
    }
    deps.requestOtp.mutate(
      { identifier: parsed.data.identifier, turnstileToken },
      {
        onSuccess: (data) => {
          deps.turnstile.resetTurnstile();
          deps.setChallenge(data);
          // G2 — OTP sign-in never shows RegistrationConsentsField (register-only).
          deps.setShowOtpConsents(
            shouldCollectOtpConsents(data.hasLegalGrant, deps.accountHasLegalGrant),
          );
        },
        onError: (err) => {
          deps.turnstile.resetTurnstile();
          const code = getApiErrorCode(err);
          if (isAccountLockedError(err)) {
            deps.setDeliveryErrorCode(undefined);
            deps.setIdentifierError(undefined);
            deps.setFormError(undefined);
            applyAccountLocked(deps, err);
          } else if (code === CustomerAuthErrorCodes.OTP_DELIVERY_FAILED) {
            clearAccountLocked(deps);
            deps.setDeliveryErrorCode(CustomerAuthErrorCodes.OTP_DELIVERY_FAILED);
            deps.setIdentifierError(undefined);
            deps.setFormError(undefined);
          } else if (code === CustomerAuthErrorCodes.SMS_NOT_AVAILABLE) {
            clearAccountLocked(deps);
            deps.setDeliveryErrorCode(CustomerAuthErrorCodes.SMS_NOT_AVAILABLE);
            deps.setIdentifierError(undefined);
            deps.setFormError(undefined);
          } else if (isApiErrorCode(err, CustomerAuthErrorCodes.EMAIL_NOT_VERIFIED)) {
            // G8 — dedicated hint only (no field errorText for the same failure class).
            clearAccountLocked(deps);
            deps.setDeliveryErrorCode(undefined);
            deps.setIdentifierError(undefined);
            deps.setFormError(undefined);
            deps.setEmailNotVerified(true);
          } else {
            // G4 — API/form-level → banner only (not identifierError / FormErrorSummary).
            clearAccountLocked(deps);
            deps.setDeliveryErrorCode(undefined);
            deps.setIdentifierError(undefined);
            deps.setFormError(toCustomerFacingErrorMessage(err));
          }
        },
      },
    );
  };

  const handleVerify = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    deps.setCodeError(undefined);
    deps.setFormError(undefined);
    deps.setConsentFieldErrors({});
    clearAccountLocked(deps);
    if (deps.challenge === null) {
      return;
    }
    const parsed = verifyOtpSchema.safeParse({
      jti: deps.challenge.jti,
      code: deps.code,
    });
    if (!parsed.success) {
      deps.setCodeError(parsed.error.issues[0]?.message ?? deps.t('validation.genericInvalid'));
      return;
    }

    let consentsPayload: ReturnType<typeof toAuthConsentsPayload> | undefined;
    if (deps.showOtpConsents) {
      const consentsParsed = otpConsentsSchema.safeParse(deps.otpConsents);
      if (!consentsParsed.success) {
        deps.setConsentFieldErrors(buildConsentFieldErrors(consentsParsed.error.issues));
        return;
      }
      consentsPayload = toAuthConsentsPayload(consentsParsed.data);
    }

    const turnstileToken = await resolveTurnstileToken(deps.turnstile);
    if (deps.turnstile.required && (turnstileToken === undefined || turnstileToken.length === 0)) {
      return;
    }
    try {
      const result = await deps.verifyOtp.mutateAsync({
        ...parsed.data,
        persistRefreshSession: deps.showRememberMe ? deps.rememberMe : true,
        ...(consentsPayload !== undefined ? { consents: consentsPayload } : {}),
        turnstileToken,
        ...(deps.challenge.tenantCode !== undefined && deps.challenge.tenantCode.length > 0
          ? { authTenantCode: deps.challenge.tenantCode }
          : {}),
      });
      deps.turnstile.resetTurnstile();
      // G1 — useVerifyOtp may resolve PENDING_OTHER_CHANNEL / non-full without land.
      if (isPendingOtherChannelResponse(result)) {
        const next = buildRemainingPendingRegistration({
          current: readPendingRegistration(deps.tenantCode),
          remainingChannel: result.pendingChannel,
          maskedPendingIdentifier: result.maskedPendingIdentifier,
        });
        savePendingRegistration(deps.tenantCode, next);
        deps.setChallenge(null);
        deps.navigate(resolveCustomerSignUpPendingPath(deps.tenantCode), {
          replace: true,
          state: next,
        });
        return;
      }
      if (isFullAuthSessionResponse(result)) {
        // Full session only — mutateAsync already landed cookies + establishSession.
        // Pass customerId: React session state may not have re-rendered yet.
        deps.onSignedIn?.(result.customerId);
      }
    } catch (err) {
      deps.turnstile.resetTurnstile();
      const error = err instanceof Error ? err : new Error(String(err));
      if (isAccountLockedError(error)) {
        deps.setCodeError(undefined);
        deps.setFormError(undefined);
        applyAccountLocked(deps, error);
      } else if (isApiErrorCode(error, CustomerAuthErrorCodes.EMAIL_NOT_VERIFIED)) {
        // G8 — dedicated hint only (no code field errorText).
        clearAccountLocked(deps);
        deps.setCodeError(undefined);
        deps.setFormError(undefined);
        deps.setEmailNotVerified(true);
      } else if (isApiErrorCode(error, CustomerAuthErrorCodes.LEGAL_CONSENTS_REQUIRED)) {
        // G2 — mirror password G3: no inline OTP ticks; form banner + navigate to Registrace.
        clearAccountLocked(deps);
        deps.setShowOtpConsents(false);
        deps.setCodeError(undefined);
        deps.setFormError(deps.t('auth.legalConsentsRequired'));
        deps.navigate(resolveCustomerSignUpPath(deps.tenantCode));
      } else {
        // G4 — API/form-level → banner only (not codeError / FormErrorSummary).
        clearAccountLocked(deps);
        deps.setCodeError(undefined);
        deps.setFormError(toCustomerFacingErrorMessage(error));
      }
    }
  };

  const handlePasswordLogin = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    deps.setLastAuthProvider('password');
    deps.setIdentifierError(undefined);
    deps.setPasswordError(undefined);
    deps.setFormError(undefined);
    deps.setCredentialsNotConfigured(false);
    deps.setEmailNotVerified(false);
    clearAccountLocked(deps);
    // G3 — password login never collects legal ticks (register-only via Registrace).
    const parsed = createLoginPasswordSchema(deps.passwordPhoneLookupPolicy).safeParse({
      identifier: deps.identifier,
      password: deps.password,
    });
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        if (issue.path[0] === 'identifier') {
          deps.setIdentifierError(issue.message);
        }
        if (issue.path[0] === 'password') {
          deps.setPasswordError(issue.message);
        }
      }
      return;
    }
    const classified = classifyPasswordLoginIdentifier(
      parsed.data.identifier,
      deps.passwordPhoneLookupPolicy,
    );
    if (classified === null) {
      deps.setIdentifierError(
        deps.t('validation.identifier.invalid', { ns: 'customer' }),
      );
      return;
    }
    const turnstileToken = await resolveTurnstileToken(deps.turnstile);
    if (deps.turnstile.required && (turnstileToken === undefined || turnstileToken.length === 0)) {
      return;
    }
    try {
      const data = await deps.loginPassword.mutateAsync({
        identifier: classified.identifier,
        password: parsed.data.password,
        persistRefreshSession: deps.showRememberMe ? deps.rememberMe : true,
        turnstileToken,
      });
      // G2 — mutateAsync resolves only after cookie land + establishSession.
      // G1 — only full session responses fire onSignedIn.
      deps.turnstile.resetTurnstile();
      if (isFullAuthSessionResponse(data)) {
        // Pass customerId: React session state may not have re-rendered yet.
        deps.onSignedIn?.(data.customerId);
      }
    } catch (err) {
      deps.turnstile.resetTurnstile();
      const error = err instanceof Error ? err : new Error(String(err));
      if (isAccountLockedError(error)) {
        deps.setCredentialsNotConfigured(false);
        deps.setPasswordError(undefined);
        deps.setFormError(undefined);
        applyAccountLocked(deps, error);
      } else if (isApiErrorCode(error, CustomerAuthErrorCodes.CREDENTIALS_NOT_CONFIGURED)) {
        clearAccountLocked(deps);
        deps.setCredentialsNotConfigured(true);
        deps.setPasswordError(undefined);
        deps.setFormError(undefined);
      } else if (isApiErrorCode(error, CustomerAuthErrorCodes.EMAIL_NOT_VERIFIED)) {
        // G8 — dedicated hint only (no password/identifier field errorText).
        clearAccountLocked(deps);
        deps.setCredentialsNotConfigured(false);
        deps.setPasswordError(undefined);
        deps.setIdentifierError(undefined);
        deps.setFormError(undefined);
        deps.setEmailNotVerified(true);
      } else if (isApiErrorCode(error, CustomerAuthErrorCodes.LEGAL_CONSENTS_REQUIRED)) {
        // G3 — no inline ticks; G4 — form banner + navigate to Registrace (sign-up).
        clearAccountLocked(deps);
        deps.setShowPasswordConsents(false);
        deps.setPasswordError(undefined);
        deps.setFormError(deps.t('auth.legalConsentsRequired'));
        deps.navigate(resolveCustomerSignUpPath(deps.tenantCode));
      } else {
        // G4 — generic API → form banner only (public copy only — never Invalid JSON / HTTP codes).
        clearAccountLocked(deps);
        deps.setPasswordError(undefined);
        deps.setFormError(toCustomerFacingErrorMessage(error));
      }
    }
  };

  return { goToPendingVerification, handleRequest, handleVerify, handlePasswordLogin };
}
